import { HOST_URL } from '../../config'
import { HttpStatus } from '../../constants/http-status.enum'
import type { ApiResponse } from '../index'
import type { IClientUID } from '../clients'

export enum StartErrorCode {
    Ok = 0,
    AlreadyPatching = 1,
    NotInSync = 2,
}

export async function startClientFirmwarePatch(clientId: IClientUID, base64Bin: string): Promise<ApiResponse<StartErrorCode>> {
    const response = await fetch(`${HOST_URL}ota`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, bin: base64Bin }),
    })
    const result = (await response.json()) as any
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export type HttpOtaResult = {
    success: number
    bytesWritten: number
    durationMs: number
    message: string
}

export type HttpOtaStatus = {
    state: number
    bytesWritten: number
    totalBytes: number
    progressPercent: number
    message: string
}

export type HttpOtaCancelResult = {
    accepted: number
    message: string
}

export async function startClientHttpFirmwarePatch(clientId: IClientUID, firmwareUrl: string): Promise<ApiResponse<HttpOtaResult>> {
    const response = await fetch(`${HOST_URL}ota/http`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, firmwareUrl }),
    })
    const result = (await response.json()) as any
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function startClientHttpFirmwarePatchFromBin(clientId: IClientUID, base64Bin: string): Promise<ApiResponse<HttpOtaResult>> {
    const response = await fetch(`${HOST_URL}ota/http/bin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, bin: base64Bin }),
    })
    const result = (await response.json()) as any
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function getClientHttpOtaStatus(clientId: IClientUID): Promise<ApiResponse<HttpOtaStatus>> {
    const response = await fetch(`${HOST_URL}ota/http/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as any
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function cancelClientHttpOta(clientId: IClientUID): Promise<ApiResponse<HttpOtaCancelResult>> {
    const response = await fetch(`${HOST_URL}ota/http/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as any
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

// ---------------------------------------------------------------------------
// C6 (ESP32-C6 network_adapter coprocessor) raw image push (socket Flow B)
// ---------------------------------------------------------------------------

export type C6RawOtaStatus = {
    active: boolean
    progress: number
    completed: boolean
    errorMessage: string | null
    updatedAt: number
}

export async function startClientC6FirmwarePatch(clientId: IClientUID, base64Bin: string): Promise<ApiResponse<StartErrorCode>> {
    const response = await fetch(`${HOST_URL}ota/c6`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, bin: base64Bin }),
    })
    const result = (await response.json()) as any
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function stopClientC6FirmwarePatch(clientId: IClientUID): Promise<ApiResponse<undefined>> {
    const response = await fetch(`${HOST_URL}ota/c6/abort`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as any
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function getClientC6RawOtaStatus(clientId: IClientUID): Promise<ApiResponse<C6RawOtaStatus>> {
    const response = await fetch(`${HOST_URL}ota/c6/push-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as any
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

// ---------------------------------------------------------------------------
// C6 (ESP32-C6 network_adapter coprocessor) HTTP-OTA URL handoff
// ---------------------------------------------------------------------------

export async function startClientC6HttpFirmwarePatch(clientId: IClientUID, firmwareUrl: string): Promise<ApiResponse<HttpOtaResult>> {
    const response = await fetch(`${HOST_URL}ota/c6/http`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, firmwareUrl }),
    })
    const result = (await response.json()) as any
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function startClientC6HttpFirmwarePatchFromBin(clientId: IClientUID, base64Bin: string): Promise<ApiResponse<HttpOtaResult>> {
    const response = await fetch(`${HOST_URL}ota/c6/http/bin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, bin: base64Bin }),
    })
    const result = (await response.json()) as any
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function getClientC6HttpOtaStatus(clientId: IClientUID): Promise<ApiResponse<HttpOtaStatus>> {
    const response = await fetch(`${HOST_URL}ota/c6/http/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as any
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function cancelClientC6HttpOta(clientId: IClientUID): Promise<ApiResponse<HttpOtaCancelResult>> {
    const response = await fetch(`${HOST_URL}ota/c6/http/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as any
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function stopClientFirmwarePatch(clientId: IClientUID): Promise<ApiResponse<null>> {
    const response = await fetch(`${HOST_URL}ota/abort`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as any
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function isPatchingClient(clientId: IClientUID): Promise<ApiResponse<boolean>> {
    const response = await fetch(`${HOST_URL}ota/is-patching`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as any
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}

export async function isRestartingClient(clientId: IClientUID): Promise<ApiResponse<boolean>> {
    const response = await fetch(`${HOST_URL}ota/is-restarting`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId }),
    })
    const result = (await response.json()) as any
    if (response.status !== HttpStatus.Ok) throw new Error(result.message)
    return result
}
